import { Box, Button, CircularProgress, Paper, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import TuneIcon from '@mui/icons-material/Tune';
import SimpleQueryGroup from '../../../../Clients/QueryBuilder/components/SimpleQueryGroup';
import { GroupSelector } from '../../../../Clients/QueryBuilder/components/GroupSelector';
import type { QueryBuilderMode } from '../../../../Clients/QueryBuilder/components/CreateQueryTab';
import type { AvailableGroup } from '../../../../../context/QueryBuilderDataContext';
import type { TargetingBuilder } from './useTargetingBuilder';
import AdvancedRuleGroups from './AdvancedRuleGroups';

interface QueryBuilderPanelProps {
  builder: TargetingBuilder;
  defaultDescription: string;
  availableGroups: AvailableGroup[];
  loadingGroups: boolean;
  onRefetchGroups: () => void;
  onReset: () => void;
  onCount: () => void;
  counting: boolean;
}

function QueryBuilderPanel({
  builder, defaultDescription, availableGroups, loadingGroups, onRefetchGroups, onReset, onCount, counting,
}: QueryBuilderPanelProps) {
  const { mode, setMode, simple, advanced } = builder;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, next: QueryBuilderMode | null) => next && setMode(next)}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', px: 3, fontWeight: 500 },
            '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'common.white', '&:hover': { bgcolor: 'primary.dark' } },
          }}
        >
          <ToggleButton value="simple">
            <FilterAltIcon sx={{ mr: 1 }} fontSize="small" />
            Ciblage simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} fontSize="small" />
            Ciblage avancé
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            key={builder.descriptionKey}
            size="small"
            label="Nom de la requête *"
            defaultValue={defaultDescription}
            onChange={e => { builder.descriptionRef.current = e.target.value; }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <GroupSelector
            selectedGroupId={builder.selectedGroupId}
            newGroupName={builder.newGroupName}
            availableGroups={availableGroups.map(g => ({ id: g.id, description: g.description }))}
            loading={loadingGroups}
            onGroupIdChange={builder.selectGroup}
            onCreateGroup={builder.createGroup}
            onRefetchGroups={onRefetchGroups}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onReset}
              sx={{ textTransform: 'none', borderColor: 'text.secondary', color: 'text.secondary' }}
            >
              Réinit.
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={counting ? <CircularProgress size={12} color="inherit" /> : <PlayArrowIcon />}
              onClick={onCount}
              disabled={counting || !builder.isValid || !builder.hasRules}
              sx={{ textTransform: 'none', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              Compter
            </Button>
          </Box>
        </Box>

        {advanced.loadingFields ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : mode === 'simple' ? (
          <SimpleQueryGroup fields={simple.simpleFields} rules={simple.rules} onUpdateField={simple.updateField} />
        ) : (
          <AdvancedRuleGroups builder={advanced} />
        )}
      </Paper>
    </>
  );
}

export default QueryBuilderPanel;
